import React, { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { PageHeader } from '../../components/molecules';

interface InventoryItem {
  id: string;
  name: string;
  category: string;
  sku: string;
  quantity: number;
  minQuantity: number;
  unit: string;
  unitCost: number;
  unitPrice: number;
  supplier: string;
  location: string;
  expiryDate: string;
  description: string;
  lastRestocked: string;
}

const categories = [
  'Medication',
  'Medical Supplies',
  'Supplements',
  'Food',
  'Toys',
  'Grooming',
  'Cleaning Supplies',
  'Equipment'
];

const units = [
  'tablets',
  'capsules',
  'ml',
  'bottles',
  'boxes',
  'pairs',
  'bags',
  'applicators',
  'units',
  'kits'
];

const Inventory: React.FC = () => {
  const [items, setItems] = useState<InventoryItem[]>([
    {
      id: '1',
      name: 'Heartgard Plus',
      category: 'Medication',
      sku: 'MED-001',
      quantity: 45,
      minQuantity: 20,
      unit: 'tablets',
      unitCost: 15.5,
      unitPrice: 25.0,
      supplier: 'PetMed Supply',
      location: 'Cabinet A-1',
      expiryDate: '2024-12-31',
      description: 'Heartworm prevention for dogs',
      lastRestocked: '2024-01-10'
    },
    {
      id: '2',
      name: 'Frontline Plus',
      category: 'Medication',
      sku: 'MED-002',
      quantity: 8,
      minQuantity: 15,
      unit: 'applicators',
      unitCost: 22,
      unitPrice: 35,
      supplier: 'PetMed Supply',
      location: 'Cabinet A-2',
      expiryDate: '2024-08-15',
      description: 'Flea and tick prevention',
      lastRestocked: '2024-01-05'
    },
    {
      id: '3',
      name: 'Surgical Gloves',
      category: 'Medical Supplies',
      sku: 'SUP-001',
      quantity: 150,
      minQuantity: 50,
      unit: 'pairs',
      unitCost: 0.5,
      unitPrice: 2,
      supplier: 'Medical Depot',
      location: 'Drawer B-1',
      expiryDate: '2025-06-30',
      description: 'Latex surgical gloves, size M',
      lastRestocked: '2024-01-12'
    },
    {
      id: '4',
      name: 'Vitamin E Supplements',
      category: 'Supplements',
      sku: 'SUP-002',
      quantity: 25,
      minQuantity: 30,
      unit: 'bottles',
      unitCost: 8,
      unitPrice: 15,
      supplier: 'HealthPet Inc',
      location: 'Shelf C-3',
      expiryDate: '2024-03-20',
      description: 'Dietary supplements for dogs',
      lastRestocked: '2023-12-15'
    }
  ]);

  const [showForm, setShowForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [stockFilter, setStockFilter] = useState<'all' | 'low' | 'out'>('all');

  const [formData, setFormData] = useState({
    name: '',
    category: 'Medication',
    sku: '',
    quantity: 0,
    minQuantity: 0,
    unit: '',
    unitCost: 0,
    unitPrice: 0,
    supplier: '',
    location: '',
    expiryDate: '',
    description: ''
  });

  const filteredItems = useMemo(
    () =>
      items.filter((item) => {
        const matchesSearch =
          item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.sku.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.supplier.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesCategory = !categoryFilter || item.category === categoryFilter;
        const matchesStock =
          stockFilter === 'all'
            ? true
            : stockFilter === 'out'
            ? item.quantity === 0
            : item.quantity <= item.minQuantity;
        return matchesSearch && matchesCategory && matchesStock;
      }),
    [items, searchTerm, categoryFilter, stockFilter]
  );

  const uniqueCategories = useMemo(() => Array.from(new Set(items.map((item) => item.category))), [items]);

  const totalValue = useMemo(
    () => items.reduce((sum, item) => sum + item.quantity * item.unitCost, 0),
    [items]
  );

  const lowStockCount = useMemo(
    () => items.filter((item) => item.quantity <= item.minQuantity).length,
    [items]
  );

  const outOfStockCount = useMemo(() => items.filter((item) => item.quantity === 0).length, [items]);

  const getStockStatus = (item: InventoryItem) => {
    if (item.quantity === 0) {
      return {
        label: 'Out of stock',
        badge: 'bg-rose-100 text-rose-600 border border-rose-200',
        accent: 'text-rose-500'
      };
    }
    if (item.quantity <= item.minQuantity) {
      return {
        label: 'Low stock',
        badge: 'bg-amber-100 text-amber-600 border border-amber-200',
        accent: 'text-amber-500'
      };
    }
    return {
      label: 'In stock',
      badge: 'bg-emerald-100 text-emerald-600 border border-emerald-200',
      accent: 'text-emerald-500'
    };
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const newItem: InventoryItem = {
        id: Date.now().toString(),
        ...formData,
        lastRestocked: new Date().toISOString().split('T')[0]
      };
      setItems((prev) => [...prev, newItem]);
      setFormData({
        name: '',
        category: 'Medication',
        sku: '',
        quantity: 0,
        minQuantity: 0,
        unit: '',
        unitCost: 0,
        unitPrice: 0,
        supplier: '',
        location: '',
        expiryDate: '',
        description: ''
      });
      setShowForm(false);
      toast.success(`Item ${newItem.name} added to inventory.`);
    } catch (err) {
      toast.error('Unable to add item. Please try again.');
    }
  };

  const handleUpdateQuantity = (id: string, newQuantity: number) => {
    try {
      setItems((prev) =>
        prev.map((item) =>
          item.id === id ? { ...item, quantity: Math.max(0, newQuantity) } : item
        )
      );
      const updated = items.find((item) => item.id === id);
      if (updated) {
        toast.success(`Updated quantity for ${updated.name}.`);
      }
    } catch (err) {
      toast.error('Failed to update quantity. Please try again.');
    }
  };

  const handleDelete = (id: string) => {
    try {
      const removed = items.find((item) => item.id === id);
      setItems((prev) => prev.filter((item) => item.id !== id));
      if (removed) {
        toast.success(`Removed ${removed.name} from inventory.`);
      }
    } catch (err) {
      toast.error('Failed to delete item. Please try again.');
    }
  };

  const filtersActive = Boolean(searchTerm) || Boolean(categoryFilter) || stockFilter !== 'all';
  const filterSummary = filtersActive
    ? [
        searchTerm ? `Query: "${searchTerm}"` : null,
        categoryFilter ? `Category: ${categoryFilter}` : null,
        stockFilter !== 'all' ? `Stock: ${stockFilter}` : null
      ]
        .filter(Boolean)
        .join(' · ')
    : 'Entire catalog';

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow="Stockroom"
        title="Inventory"
        description="Monitor stock levels, identify low inventory, and plan restocks with confidence."
        icon="📦"
        accent="emerald"
        actions={(
          <button
            className="inline-flex items-center gap-2 rounded-full bg-brand-primary px-5 py-2.5 text-sm font-semibold text-white shadow-soft transition hover:bg-brand-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-accent"
            onClick={() => setShowForm(true)}
          >
            <span className="text-lg leading-none">➕</span>
            Add inventory item
          </button>
        )}
        stats={[
          { label: 'Items', value: items.length, helper: 'Active SKUs' },
          {
            label: 'Inventory value',
            value: new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(totalValue),
            helper: 'Cost on hand'
          },
          { label: 'Low stock', value: lowStockCount, helper: 'At threshold' },
          { label: 'Out of stock', value: outOfStockCount, helper: 'Needs restock' }
        ]}
      >
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
          <label className="flex w-full flex-col gap-2 text-sm font-semibold text-slate-600">
            Search items
            <input
              type="text"
              placeholder="Search by name, SKU, or supplier…"
              className="mt-2 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 placeholder:text-slate-400 focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/20"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </label>

          <div className="grid gap-4 md:grid-cols-2">
            <label className="flex flex-col gap-2 text-sm font-semibold text-slate-600">
              Category
              <select
                className="mt-2 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/20"
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                <option value="">All categories</option>
                {uniqueCategories.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-2 text-sm font-semibold text-slate-600">
              Stock status
              <select
                className="mt-2 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/20"
                value={stockFilter}
                onChange={(e) => setStockFilter(e.target.value as typeof stockFilter)}
              >
                <option value="all">All stock levels</option>
                <option value="low">Low stock</option>
                <option value="out">Out of stock</option>
              </select>
            </label>
          </div>
        </div>
        <p className="mt-3 text-xs text-slate-500">{filterSummary}</p>
      </PageHeader>

      <section className="space-y-4">
        {filteredItems.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-brand-muted bg-white/70 px-8 py-16 text-center shadow-soft">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-brand-primary/10 text-3xl text-brand-primary">
              🧮
            </div>
            <h3 className="mt-6 text-lg font-semibold text-slate-700">
              No inventory items match your filters
            </h3>
            <p className="mt-2 text-sm text-slate-500">
              Adjust filters or add a new product to your shelves.
            </p>
          </div>
        ) : (
          <div className="grid gap-4 xl:grid-cols-2">
            {filteredItems.map((item) => {
              const stock = getStockStatus(item);
              const totalValue = item.quantity * item.unitCost;

              return (
                <article
                  key={item.id}
                  className="flex h-full flex-col gap-6 rounded-3xl border border-slate-200 bg-white p-6 shadow-soft transition hover:-translate-y-1 hover:shadow-xl"
                >
                  <header className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                      <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">
                        {item.sku}
                      </span>
                      <h3 className="mt-3 text-lg font-semibold text-slate-800">{item.name}</h3>
                      <p className="text-sm text-slate-500">{item.description}</p>
                    </div>
                    <span className={`rounded-full px-3 py-1 text-xs font-semibold ${stock.badge}`}>
                      {stock.label}
                    </span>
                  </header>

                  <div className="grid gap-4 rounded-2xl border border-slate-100 bg-slate-50/80 p-4 text-sm">
                    <div className="flex flex-wrap items-center gap-3">
                      <span className="rounded-full bg-brand-secondary/10 px-3 py-1 text-xs font-semibold text-brand-secondary">
                        {item.category}
                      </span>
                      <span className="rounded-full bg-brand-accent/10 px-3 py-1 text-xs font-semibold text-brand-accent">
                        {item.location}
                      </span>
                      <span className="rounded-full bg-slate-200 px-3 py-1 text-xs font-semibold text-slate-600">
                        Supplier: {item.supplier}
                      </span>
                    </div>
                    <div className="grid gap-3 sm:grid-cols-3">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">
                          Quantity
                        </p>
                        <div className="mt-2 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-2">
                          <button
                            className="flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 text-slate-500 transition hover:bg-slate-100 disabled:opacity-40"
                            onClick={() => handleUpdateQuantity(item.id, item.quantity - 1)}
                            disabled={item.quantity === 0}
                          >
                            −
                          </button>
                          <span className="min-w-[2ch] text-center font-semibold text-slate-700">
                            {item.quantity}
                          </span>
                          <button
                            className="flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 text-slate-500 transition hover:bg-slate-100"
                            onClick={() => handleUpdateQuantity(item.id, item.quantity + 1)}
                          >
                            +
                          </button>
                        </div>
                      </div>
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">
                          Minimum level
                        </p>
                        <p className="mt-2 text-slate-700">
                          {item.minQuantity} {item.unit}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">
                          Expiry date
                        </p>
                        <p className={`mt-2 font-semibold ${stock.accent}`}>
                          {item.expiryDate
                            ? new Date(item.expiryDate).toLocaleDateString('en-US')
                            : 'No expiry'}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="grid gap-4 rounded-2xl border border-slate-100 bg-white px-4 py-3 text-sm">
                    <div className="flex items-center justify-between text-slate-500">
                      <span>Unit cost</span>
                      <span>
                        {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
                          item.unitCost
                        )}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-slate-500">
                      <span>Unit price</span>
                      <span>
                        {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
                          item.unitPrice
                        )}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-lg font-semibold text-slate-800">
                      <span>Total value</span>
                      <span>
                        {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
                          totalValue
                        )}
                      </span>
                    </div>
                  </div>

                  <footer className="flex items-center justify-between text-xs text-slate-400">
                    <p>
                      Last restocked{' '}
                      {new Date(item.lastRestocked).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </p>
                    <button
                      className="inline-flex items-center gap-2 rounded-full border border-rose-200 bg-rose-50 px-4 py-2 text-xs font-semibold text-rose-600 transition hover:bg-rose-100"
                      onClick={() => handleDelete(item.id)}
                    >
                      Remove item
                    </button>
                  </footer>
                </article>
              );
            })}
          </div>
        )}
      </section>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 backdrop-blur-sm">
          <div className="relative w-full max-w-3xl rounded-3xl border border-slate-200 bg-white p-8 shadow-soft">
            <button
              className="absolute right-6 top-6 flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 text-lg text-slate-500 transition hover:bg-slate-100"
              onClick={() => setShowForm(false)}
              aria-label="Close form"
            >
              ✕
            </button>

            <header>
              <h2 className="text-xl font-semibold text-slate-800">Add inventory item</h2>
              <p className="mt-2 text-sm text-slate-500">
                Track unit costs, stock thresholds, and suppliers for every SKU.
              </p>
            </header>

            <form className="mt-6 space-y-4 text-sm" onSubmit={handleSubmit}>
              <div className="grid gap-4 md:grid-cols-2">
                <label className="flex flex-col gap-2 font-semibold text-slate-600">
                  Item name *
                  <input
                    type="text"
                    className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-700 focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/20"
                    value={formData.name}
                    onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                    required
                  />
                </label>
                <label className="flex flex-col gap-2 font-semibold text-slate-600">
                  SKU *
                  <input
                    type="text"
                    className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-700 focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/20"
                    value={formData.sku}
                    onChange={(e) => setFormData((prev) => ({ ...prev, sku: e.target.value }))}
                    required
                  />
                </label>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <label className="flex flex-col gap-2 font-semibold text-slate-600">
                  Category *
                  <select
                    className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-700 focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/20"
                    value={formData.category}
                    onChange={(e) => setFormData((prev) => ({ ...prev, category: e.target.value }))}
                    required
                  >
                    {categories.map((category) => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="flex flex-col gap-2 font-semibold text-slate-600">
                  Unit of measure *
                  <input
                    list="units"
                    className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-700 focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/20"
                    value={formData.unit}
                    onChange={(e) => setFormData((prev) => ({ ...prev, unit: e.target.value }))}
                    required
                  />
                  <datalist id="units">
                    {units.map((unit) => (
                      <option key={unit} value={unit} />
                    ))}
                  </datalist>
                </label>
              </div>

              <div className="grid gap-4 md:grid-cols-3">
                <label className="flex flex-col gap-2 font-semibold text-slate-600">
                  Quantity *
                  <input
                    type="number"
                    min="0"
                    className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-700 focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/20"
                    value={formData.quantity}
                    onChange={(e) => setFormData((prev) => ({ ...prev, quantity: Number(e.target.value) }))}
                    required
                  />
                </label>
                <label className="flex flex-col gap-2 font-semibold text-slate-600">
                  Minimum level *
                  <input
                    type="number"
                    min="0"
                    className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-700 focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/20"
                    value={formData.minQuantity}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, minQuantity: Number(e.target.value) }))
                    }
                    required
                  />
                </label>
                <label className="flex flex-col gap-2 font-semibold text-slate-600">
                  Expiry date
                  <input
                    type="date"
                    className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-700 focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/20"
                    value={formData.expiryDate}
                    onChange={(e) => setFormData((prev) => ({ ...prev, expiryDate: e.target.value }))}
                  />
                </label>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <label className="flex flex-col gap-2 font-semibold text-slate-600">
                  Supplier
                  <input
                    type="text"
                    className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-700 focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/20"
                    value={formData.supplier}
                    onChange={(e) => setFormData((prev) => ({ ...prev, supplier: e.target.value }))}
                  />
                </label>
                <label className="flex flex-col gap-2 font-semibold text-slate-600">
                  Storage location
                  <input
                    type="text"
                    className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-700 focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/20"
                    value={formData.location}
                    onChange={(e) => setFormData((prev) => ({ ...prev, location: e.target.value }))}
                  />
                </label>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <label className="flex flex-col gap-2 font-semibold text-slate-600">
                  Unit cost (USD)
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-700 focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/20"
                    value={formData.unitCost}
                    onChange={(e) => setFormData((prev) => ({ ...prev, unitCost: Number(e.target.value) }))}
                  />
                </label>
                <label className="flex flex-col gap-2 font-semibold text-slate-600">
                  Unit price (USD)
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-700 focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/20"
                    value={formData.unitPrice}
                    onChange={(e) => setFormData((prev) => ({ ...prev, unitPrice: Number(e.target.value) }))}
                  />
                </label>
              </div>

              <label className="flex flex-col gap-2 font-semibold text-slate-600">
                Description
                <textarea
                  rows={3}
                  className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-700 focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/20"
                  value={formData.description}
                  onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                  placeholder="Describe the item, usage instructions, or storage notes."
                />
              </label>

              <div className="flex flex-col gap-3 pt-4 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  className="inline-flex items-center justify-center rounded-full border border-slate-200 px-5 py-2.5 text-sm font-semibold text-slate-600 transition hover:bg-slate-100"
                  onClick={() => setShowForm(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="inline-flex items-center justify-center rounded-full bg-brand-primary px-6 py-2.5 text-sm font-semibold text-white shadow-soft transition hover:bg-brand-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-accent"
                >
                  Save item
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Inventory;
